import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { Download as DownloadIcon } from '@mui/icons-material';
import type React from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import assetsByCollectionIdPlaylist from 'src/api/collection/assetsByCollectionIdPlaylist';
import editCollection from 'src/api/collection/editCollection';
import BatchDownloadDialog from 'src/components/dialogs/BatchDownloadDialog';
import type { ActionItem } from 'src/constants';
import {
  ADD_TO_GALLERY_SLUG,
  ADD_TO_PLAYLIST_SLUG,
  CAN_DELETE_SLUG,
  DOWNLOAD_SELECTED,
  DOWNLOAD_SELECTED_SLUG,
  HIRES_SLUG,
  MANAGE_GALLERY_SLUG,
  MENU_GROUPS,
  MULTI_ACTION,
  SELECTED_ITEMS,
  USER_PERMISSONS_TO_ACTIONS_MAP,
} from 'src/constants';
import CreatePlayListContext from 'src/context/CreatePlayListContext';
import { buildDownloadAvailabilityQ } from 'src/dynamoQueries/download';
import { gatherActionsFromPerms } from 'src/helpers/permissionsHelper';
import { useAssetDownload } from 'src/hooks/download';
import viewStateVar from 'src/store/viewState';
import BasePaginationActions from './BasePaginationActions';

interface AvailabilityItem {
  formatType: string;
  formatLabel?: string;
  available: number;
  total: number;
}

interface AvailabilityMap {
  [code: string]: { available: number; total: number };
}

interface AvailabilityOption {
  value: string;
  label: string;
}

interface TopPaginationActionsProps {
  permissions?: Record<string, boolean>;
}

const TopPaginationActions: React.FC<TopPaginationActionsProps> = ({ permissions }) => {
  const viewState = useReactiveVar(viewStateVar);
  const defaultActions = MULTI_ACTION;
  const [actions, setActions] = useState<ActionItem[]>(defaultActions);
  const [openDownloadDialog, setOpenDownloadDialog] = useState<boolean>(false);
  const [availabilitySelected, setAvailabilitySelected] = useState<AvailabilityMap | null>(null);
  const [availabilityOptions, setAvailabilityOptions] = useState<AvailabilityOption[] | null>(null);
  const { setCreateAddedAssets } = useContext(CreatePlayListContext);
  const { handleAssetDownload } = useAssetDownload(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: defaultActions is a constant defined outside the effect
  useEffect(() => {
    if (permissions) {
      const userPermActions = gatherActionsFromPerms(permissions, USER_PERMISSONS_TO_ACTIONS_MAP);
      setActions([...defaultActions, ...(userPermActions as ActionItem[])]);
    }
  }, [permissions]);

  const [addItemHandler] = useMutation(editCollection, {
    fetchPolicy: 'network-only',
    refetchQueries: [assetsByCollectionIdPlaylist],
  });

  const [getAvailability, { loading: availabilityLoading }] = useLazyQuery<{
    downloadAvailability: AvailabilityItem[];
  }>(buildDownloadAvailabilityQ(), {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) => {
      try {
        const list = data?.downloadAvailability || [];
        const map: AvailabilityMap = list.reduce((acc, item) => {
          const code = String(item.formatType);
          acc[code] = { available: item.available, total: item.total };
          return acc;
        }, {} as AvailabilityMap);
        const opts: AvailabilityOption[] = list.map((item) => ({
          value: String(item.formatType),
          label: item.formatLabel || String(item.formatType),
        }));
        setAvailabilitySelected(map);
        setAvailabilityOptions(opts);
      } catch (_e) {
        // Advisory only; still open and allow user to choose
      }
    },
  });

  const addItemCollectionHandler = (selectedAssetForAdding: string[]): void => {
    addItemHandler({
      variables: {
        collectionId: viewState.selectedCollectionId,
        assetIds: selectedAssetForAdding,
        collectionType: 'PLAYLIST',
        autoSeq: true,
      },
    });
  };

  const takeAction = (option: ActionItem): void => {
    switch (option.slug) {
      case MANAGE_GALLERY_SLUG:
      case ADD_TO_GALLERY_SLUG:
        viewStateVar({ ...viewState, globalAction: option.slug ?? null });
        break;
      case DOWNLOAD_SELECTED_SLUG: {
        const selected = viewStateVar()?.selectedAssets || [];
        if (selected.length === 0) return;
        setOpenDownloadDialog(true);
        getAvailability({ variables: { globalIds: selected } });
        break;
      }
      case ADD_TO_PLAYLIST_SLUG:
        handleAddToPlaylist();
        break;
      case CAN_DELETE_SLUG:
        viewStateVar({ ...viewState, deleteDialog: true });
        break;
      case HIRES_SLUG:
        viewStateVar({ ...viewStateVar(), hiresDialog: SELECTED_ITEMS });
        break;
    }
  };

  const handleAddToPlaylist = (): void => {
    const newViewState = {
      ...viewState,
      selectedAssets: [] as string[],
      isSelectAll: false,
      showRightSidebar: true,
      rightDrawerWidth: viewState.showRightSidebar ? viewState.defaultRightDrawerWidth : viewState.minRightDrawerWidth,
    };

    if (viewState.openEditCollection) {
      viewStateVar(newViewState);
      addItemCollectionHandler(viewState.selectedAssets);
      return;
    }

    setCreateAddedAssets((ids) => [
      ...ids,
      ...viewState.selectedAssets.map((id) => ({ id }) as import('src/context/CreatePlayListContext').PlaylistAsset),
    ]);
    newViewState.openNewCollection = true;
    viewStateVar(newViewState);
  };

  const modifiedActions = useMemo(() => {
    const selectedCount = (viewStateVar()?.selectedAssets || []).length;
    const downloadAction: ActionItem = {
      label: `${DOWNLOAD_SELECTED}${selectedCount > 0 ? ` (${selectedCount})` : ''}`,
      icon: <DownloadIcon sx={{ color: 'text.primary' }} />,
      slug: DOWNLOAD_SELECTED_SLUG,
      group: MENU_GROUPS.EXPORT,
    };
    return [downloadAction, ...actions];
  }, [actions]);

  return (
    <>
      <BasePaginationActions actions={modifiedActions} onActionClick={takeAction} />
      <BatchDownloadDialog
        isOpen={openDownloadDialog}
        availability={availabilitySelected}
        options={availabilityOptions}
        loading={availabilityLoading}
        onConfirm={({ formatType }) => {
          const selected = viewStateVar()?.selectedAssets || [];
          setOpenDownloadDialog(false);
          if (formatType) {
            handleAssetDownload(selected, { formatType, fallbackPolicy: 'SKIP_MISSING' });
          }
        }}
        onCancel={() => setOpenDownloadDialog(false)}
      />
    </>
  );
};

export default TopPaginationActions;
